import { create } from 'zustand'
import type { AssetClass } from '@/types'
import type { UnderwritingModel, UnderwritingField } from '@/types/underwritingModels'
import { UNDERWRITING_MODELS } from '@/data/underwritingModelDefinitions'

interface UnderwritingModelState {
  /** All model definitions keyed by asset class */
  models: Record<string, UnderwritingModel>
  /** Currently selected asset class */
  activeAssetClass: AssetClass
  /** Deal data: dealId -> fieldId -> value */
  dealData: Record<string, Record<string, number | string>>
  /** Whether the model editor is open */
  editorOpen: boolean

  setActiveAssetClass: (ac: AssetClass) => void
  getActiveModel: () => UnderwritingModel | undefined
  updateField: (dealId: string, fieldId: string, value: number | string) => void
  lockModel: (assetClass: string, userId: string) => void
  unlockModel: (assetClass: string) => void
  isModelLocked: (assetClass: string) => boolean
  toggleEditor: () => void
  addFieldToModel: (assetClass: string, sectionId: string, field: UnderwritingField) => void
  removeFieldFromModel: (assetClass: string, sectionId: string, fieldId: string) => void
}

export const useUnderwritingModelStore = create<UnderwritingModelState>((set, get) => ({
  models: { ...UNDERWRITING_MODELS },
  activeAssetClass: 'Multifamily',
  dealData: {},
  editorOpen: false,

  setActiveAssetClass: (ac) => set({ activeAssetClass: ac }),

  getActiveModel: () => get().models[get().activeAssetClass],

  updateField: (dealId, fieldId, value) =>
    set((s) => ({
      dealData: {
        ...s.dealData,
        [dealId]: {
          ...(s.dealData[dealId] ?? {}),
          [fieldId]: value,
        },
      },
    })),

  lockModel: (assetClass, userId) =>
    set((s) => {
      const model = s.models[assetClass]
      if (!model) return s
      return {
        models: {
          ...s.models,
          [assetClass]: {
            ...model,
            locked: true,
            lockedBy: userId,
            lockedAt: new Date().toISOString(),
          },
        },
      }
    }),

  unlockModel: (assetClass) =>
    set((s) => {
      const model = s.models[assetClass]
      if (!model) return s
      return {
        models: {
          ...s.models,
          [assetClass]: {
            ...model,
            locked: false,
            lockedBy: undefined,
            lockedAt: undefined,
          },
        },
      }
    }),

  isModelLocked: (assetClass) => get().models[assetClass]?.locked ?? false,

  toggleEditor: () => set((s) => ({ editorOpen: !s.editorOpen })),

  addFieldToModel: (assetClass, sectionId, field) =>
    set((s) => {
      const model = s.models[assetClass]
      if (!model || model.locked) return s
      return {
        models: {
          ...s.models,
          [assetClass]: {
            ...model,
            lastModified: new Date().toISOString().slice(0, 10),
            sections: model.sections.map((sec) =>
              sec.id === sectionId ? { ...sec, fields: [...sec.fields, field] } : sec
            ),
          },
        },
      }
    }),

  removeFieldFromModel: (assetClass, sectionId, fieldId) =>
    set((s) => {
      const model = s.models[assetClass]
      if (!model || model.locked) return s
      return {
        models: {
          ...s.models,
          [assetClass]: {
            ...model,
            lastModified: new Date().toISOString().slice(0, 10),
            sections: model.sections.map((sec) =>
              sec.id === sectionId
                ? { ...sec, fields: sec.fields.filter((f) => f.id !== fieldId) }
                : sec
            ),
          },
        },
      }
    }),
}))
